/**
 * Mappa clienti — vista geografica con pin proporzionali al fatturato.
 *
 * Tre filtri (toggle, mutuamente esclusivi):
 *  - Totale offerte: TUTTI i preventivi (qualsiasi stato)
 *  - Solo vinti: preventivi in stato `won`
 *  - Pipeline aperta: preventivi `draft` + `sent`
 *
 * Il pin size è proporzionale alla radice quadrata dell'amount filtrato,
 * normalizzato sul max della finestra corrente. Permette di confrontare
 * a colpo d'occhio "dove offro tanto vs dove chiudo davvero".
 *
 * Quando lo zoom e' basso (vista nazionale) i pin vicini vengono raggruppati
 * in cluster con il count totale e la somma euro aggregata. Click sul
 * cluster fa zoom-in fino a scioglierlo.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { MapPin, Loader2, ArrowLeft, RefreshCw, Trash2 } from 'lucide-react';
import EmptyState from '@/components/common/EmptyState';
import {
  useCustomersMap,
  useGeocodePendingCustomers,
  type CustomerMapPoint,
} from '@/hooks/useCustomersMap';
import {
  buildPieIcon, euroShort, STATUS_COLOR, STATUS_LABEL, STATUS_ORDER,
  type StatusKey,
} from '@/utils/map/pieIcon';
import {
  DEFAULT_STATUS_FILTER, type StatusFilter,
} from '@/components/customers/CustomersMapInline';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import AppSidebar from '@/components/layout/AppSidebar';
import Header from '@/components/layout/Header';
import {
  isPointInZone, loadZoneFromStorage, saveZoneToStorage, type DrawnZone,
} from '@/utils/map/zoneFilter';
import { useHasFeature } from '@/hooks/useOrgPlan';
import { useCanWrite } from '@/hooks/useCanWrite';
import { UpsellBanner, WriteBlockedBanner } from '@/components/billing/UpsellTooltip';
import { formatNumber } from '@/utils/format';

const ITALY_CENTER: [number, number] = [42.5, 12.5];

function euro(v: number): string {
  if (v >= 1_000_000) return `€ ${formatNumber(v / 1_000_000, 1)}M`;
  if (v >= 1000) return `€ ${formatNumber(v / 1000, 1)}k`;
  return `€ ${Math.round(v)}`;
}

function euroFull(v: number): string {
  if (!Number.isFinite(v) || v === 0) return '€ 0';
  return v.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });
}

function visibleTotal(c: CustomerMapPoint, filter: StatusFilter): number {
  let sum = 0;
  for (const k of STATUS_ORDER) {
    if (filter[k]) sum += c.byStatus[k].amount;
  }
  return sum;
}

const MIN_RADIUS = 12;
const MAX_RADIUS = 30;

/**
 * Sotto questa soglia il geocoding parte automatico all'apertura della
 * mappa (utente non aspetta troppo). Sopra, si chiede conferma con il
 * bottone esplicito per non bloccare la UX con un batch lungo.
 */
const AUTO_GEOCODE_THRESHOLD = 30;

/**
 * Controlli Geoman per disegnare/modificare/eliminare una zona sulla mappa.
 * Vincoli scelti:
 *  - una zona alla volta: se l'utente disegna una nuova forma, la vecchia
 *    viene rimossa (semplifica i KPI e l'UX).
 *  - Cerchio, Poligono, Rettangolo. Niente marker/polyline/text che non
 *    hanno senso per una "zona".
 *  - Toolbar in alto a destra (i controlli di zoom Leaflet stanno a sx).
 */
interface GeomanControlsProps {
  initialZone: DrawnZone | null;
  onZoneChange: (z: DrawnZone | null) => void;
}

const GeomanControls: React.FC<GeomanControlsProps> = ({ initialZone, onZoneChange }) => {
  const map = useMap();
  const drawnLayerRef = useRef<L.Layer | null>(null);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pm = (map as any).pm;
    if (!pm) return;

    pm.addControls({
      position: 'topright',
      drawCircle: true,
      drawPolygon: true,
      drawRectangle: true,
      drawMarker: false,
      drawCircleMarker: false,
      drawPolyline: false,
      drawText: false,
      editMode: true,
      removalMode: true,
      cutPolygon: false,
      rotateMode: false,
      dragMode: false,
    });
    // Localizzazione minima dei tooltip Geoman in IT
    pm.setLang('it');

    const removeOldLayer = () => {
      if (drawnLayerRef.current) {
        map.removeLayer(drawnLayerRef.current);
        drawnLayerRef.current = null;
      }
    };

    const extractZoneFromLayer = (layer: L.Layer, shape: string): DrawnZone | null => {
      if (shape === 'Circle') {
        const c = layer as L.Circle;
        const center = c.getLatLng();
        return {
          type: 'circle',
          center: { lat: center.lat, lng: center.lng },
          radius: c.getRadius(),
        };
      }
      if (shape === 'Polygon' || shape === 'Rectangle') {
        const p = layer as L.Polygon;
        // getLatLngs() torna LatLng[][] per i polygon (multi-ring possibile).
        // Prendiamo il primo ring esterno.
        const ringRaw = p.getLatLngs()[0] as L.LatLng[];
        const latlngs = ringRaw.map((ll) => [ll.lat, ll.lng] as [number, number]);
        return {
          type: shape === 'Rectangle' ? 'rectangle' : 'polygon',
          latlngs,
        };
      }
      return null;
    };

    // Disegna su mappa una zona caricata da localStorage al mount
    if (initialZone) {
      let layer: L.Layer | null = null;
      if (initialZone.type === 'circle') {
        layer = L.circle([initialZone.center.lat, initialZone.center.lng], {
          radius: initialZone.radius,
          color: '#3b82f6',
          fillOpacity: 0.1,
        });
      } else {
        layer = L.polygon(initialZone.latlngs, {
          color: '#3b82f6',
          fillOpacity: 0.1,
        });
      }
      if (layer) {
        layer.addTo(map);
        drawnLayerRef.current = layer;
      }
    }

    const handleCreate = (e: { layer: L.Layer; shape: string }) => {
      // Una zona alla volta: rimuovi la vecchia
      removeOldLayer();
      drawnLayerRef.current = e.layer;
      const zone = extractZoneFromLayer(e.layer, e.shape);
      onZoneChange(zone);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleEdit = (e: any) => {
      // Geoman emette pm:edit sul layer modificato; ri-estraiamo zone
      const layer = e.layer ?? e.target;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const shape = (layer as any).pm?.getShape?.() ?? '';
      const zone = extractZoneFromLayer(layer, shape);
      onZoneChange(zone);
    };

    const handleRemove = () => {
      drawnLayerRef.current = null;
      onZoneChange(null);
    };

    map.on('pm:create', handleCreate);
    map.on('pm:remove', handleRemove);
    map.on('pm:edit', handleEdit);

    return () => {
      map.off('pm:create', handleCreate);
      map.off('pm:remove', handleRemove);
      map.off('pm:edit', handleEdit);
      try { pm.removeControls(); } catch { /* noop */ }
      removeOldLayer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  return null;
};

const CustomersMap: React.FC = () => {
  const { data: customers = [], isLoading } = useCustomersMap();
  const geocodeMutation = useGeocodePendingCustomers();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(DEFAULT_STATUS_FILTER);
  const toggleStatus = (key: StatusKey) =>
    setStatusFilter(f => ({ ...f, [key]: !f[key] }));
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const hasMapDrawing = useHasFeature('mapDrawing');
  const { canWrite } = useCanWrite();

  // Zona disegnata (cerchio/poligono/rettangolo). Persiste in localStorage
  // come "zona operativa" tra sessioni. Se il piano non include mapDrawing,
  // ignoriamo qualsiasi zona pre-esistente in storage.
  const [zone, setZone] = useState<DrawnZone | null>(() =>
    hasMapDrawing ? loadZoneFromStorage() : null,
  );
  useEffect(() => {
    if (hasMapDrawing) saveZoneToStorage(zone);
  }, [zone, hasMapDrawing]);

  const withCoords = useMemo(
    () => customers.filter((c) => c.latitude != null && c.longitude != null),
    [customers],
  );
  const withoutCoords = customers.length - withCoords.length;

  const allStatusOff = !statusFilter.draft && !statusFilter.sent &&
                       !statusFilter.won && !statusFilter.lost;
  const visibleMarkers = useMemo(() => {
    if (allStatusOff) return [];
    return withCoords.filter((c) => visibleTotal(c, statusFilter) > 0);
  }, [withCoords, statusFilter, allStatusOff]);

  const maxAmount = useMemo(
    () => visibleMarkers.reduce((acc, c) => Math.max(acc, visibleTotal(c, statusFilter)), 0),
    [visibleMarkers, statusFilter],
  );

  const radiusFor = (amount: number): number => {
    if (amount <= 0) return MIN_RADIUS;
    if (maxAmount <= 0) return MIN_RADIUS;
    const norm = Math.sqrt(amount / maxAmount);
    return MIN_RADIUS + (MAX_RADIUS - MIN_RADIUS) * norm;
  };

  const handleGeocode = () => {
    setProgress({ done: 0, total: 0 });
    geocodeMutation.mutate(
      (done: number, total: number) => setProgress({ done, total }),
      {
        onSettled: () => setProgress(null),
      },
    );
  };

  // Auto-trigger: se all'apertura della mappa ci sono pochi clienti senza
  // coords, geocodifichiamo automaticamente. Lo facciamo una sola volta per
  // mount per evitare loop se Nominatim fallisce alcuni client.
  const autoTriggeredRef = useRef(false);
  useEffect(() => {
    if (autoTriggeredRef.current) return;
    if (isLoading) return;
    if (geocodeMutation.isPending) return;
    if (withoutCoords === 0) return;
    if (withoutCoords > AUTO_GEOCODE_THRESHOLD) return;
    autoTriggeredRef.current = true;
    handleGeocode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, withoutCoords]);

  // KPI aggregati dei clienti dentro la zona disegnata. Null se nessuna zona.
  const zoneStats = useMemo(() => {
    if (!zone) return null;
    const inside = withCoords.filter((c) =>
      c.latitude != null && c.longitude != null && isPointInZone(c.latitude, c.longitude, zone),
    );
    let total = 0, won = 0, pipeline = 0;
    let wonCount = 0;
    for (const c of inside) {
      total += c.totalAmount;
      won += c.wonAmount;
      pipeline += c.pipelineAmount;
      if (c.wonAmount > 0) wonCount++;
    }
    const conversionRate = inside.length > 0 ? (wonCount / inside.length) * 100 : 0;
    const top3 = [...inside].sort((a, b) => b.totalAmount - a.totalAmount).slice(0, 3);
    return { inside, count: inside.length, total, won, pipeline, conversionRate, top3 };
  }, [zone, withCoords]);

  const clearZone = () => setZone(null);

  // Cluster icon: SVG pie con il breakdown aggregato dei child marker.
  // Filtro stati applicato anche all'aggregazione.
  const buildClusterIcon = useMemo(() => {
    return (cluster: L.MarkerCluster): L.DivIcon => {
      const childMarkers = cluster.getAllChildMarkers();
      const agg: Record<StatusKey, number> = { draft: 0, sent: 0, won: 0, lost: 0 };
      let totalCount = 0;
      for (const m of childMarkers) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = (m.options as any).statusAmounts as Record<StatusKey, number> | undefined;
        if (!data) continue;
        for (const k of STATUS_ORDER) {
          if (statusFilter[k]) agg[k] += data[k];
        }
        totalCount += 1;
      }
      const total = agg.draft + agg.sent + agg.won + agg.lost;
      const r = Math.min(40, 20 + Math.log2(totalCount + 1) * 5);
      return buildPieIcon({
        radius: r,
        slices: STATUS_ORDER.map(s => ({ status: s, amount: agg[s] })),
        centerLabel: String(totalCount),
        centerSubLabel: total > 0 ? euroShort(total) : undefined,
      });
    };
  }, [statusFilter]);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <Header />
        <div className="container mx-auto px-4 py-6 space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Link to="/customers">
                <Button variant="ghost" size="sm" className="gap-1">
                  <ArrowLeft className="h-4 w-4" />
                  Clienti
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                <h1 className="text-xl font-bold tracking-tight">Mappa clienti</h1>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap text-xs bg-muted/40 rounded-md border px-3 py-1.5">
                {STATUS_ORDER.map(s => (
                  <label
                    key={s}
                    className="flex items-center gap-1.5 cursor-pointer select-none"
                  >
                    <Checkbox
                      checked={statusFilter[s]}
                      onCheckedChange={() => toggleStatus(s)}
                      className="h-3.5 w-3.5"
                    />
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full border border-gray-300"
                      style={{ background: STATUS_COLOR[s] }}
                    />
                    <span className="text-foreground">{STATUS_LABEL[s]}</span>
                  </label>
                ))}
              </div>

              {withoutCoords > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGeocode}
                  disabled={geocodeMutation.isPending}
                  className="gap-1"
                  title="Trova le coordinate dei clienti dall'indirizzo (1-2s per cliente)"
                >
                  {geocodeMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Geocodifica {withoutCoords}
                </Button>
              )}
            </div>
          </div>

          {progress && progress.total > 0 && (
            <Card>
              <CardContent className="py-3">
                <p className="text-sm text-muted-foreground">
                  Geocoding in corso… {progress.done} / {progress.total} clienti elaborati
                </p>
                <div className="mt-2 h-2 bg-muted rounded overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${(progress.done / progress.total) * 100}%` }}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {isLoading ? (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                Caricamento clienti…
              </CardContent>
            </Card>
          ) : customers.length === 0 ? (
            <EmptyState
              icon={<MapPin className="h-12 w-12" />}
              title="Nessun cliente"
              description="Crea il primo cliente per vederlo sulla mappa."
              action={<Link to="/customers"><Button>Vai a Clienti</Button></Link>}
            />
          ) : withCoords.length === 0 ? (
            <EmptyState
              icon={<MapPin className="h-12 w-12" />}
              title={
                geocodeMutation.isPending
                  ? 'Geocodifica in corso…'
                  : customers.length > AUTO_GEOCODE_THRESHOLD
                    ? 'Nessun cliente sulla mappa'
                    : 'Sto trovando le coordinate…'
              }
              description={
                geocodeMutation.isPending
                  ? `Trovo gli indirizzi su OpenStreetMap (1 cliente al secondo). ${progress ? `${progress.done}/${progress.total}` : ''}`
                  : customers.length > AUTO_GEOCODE_THRESHOLD
                    ? `Hai ${customers.length} clienti senza coordinate. Sopra ${AUTO_GEOCODE_THRESHOLD} clienti il batch richiede tempo (~${Math.round(customers.length * 1.1)}s), quindi avvialo manualmente.`
                    : `${customers.length} clienti da geocodificare. Parte automaticamente, attendi ~${Math.round(customers.length * 1.1)}s.`
              }
              action={
                customers.length > AUTO_GEOCODE_THRESHOLD && !geocodeMutation.isPending ? (
                  <Button onClick={handleGeocode}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Geocodifica {customers.length} clienti
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              {/* KPI di zona: visibile solo quando l'utente ha disegnato qualcosa */}
              {zoneStats && (
                <Card className="bg-gradient-to-br from-primary/5 to-blue-50 border-primary/30">
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-[300px]">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                          <h2 className="font-semibold text-sm">
                            Zona disegnata · {zone?.type === 'circle' ? 'Cerchio' : zone?.type === 'rectangle' ? 'Rettangolo' : 'Poligono'}
                          </h2>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 ml-auto text-muted-foreground hover:text-destructive"
                            onClick={clearZone}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            Cancella
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Clienti</div>
                            <div className="text-xl font-bold">{zoneStats.count}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Totale offerte</div>
                            <div className="text-xl font-bold">{euro(zoneStats.total)}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Vinti</div>
                            <div className="text-xl font-bold text-green-700">{euro(zoneStats.won)}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pipeline</div>
                            <div className="text-xl font-bold text-amber-700">{euro(zoneStats.pipeline)}</div>
                          </div>
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Conv. rate</div>
                            <div className="text-xl font-bold">{formatNumber(zoneStats.conversionRate, 0)}%</div>
                          </div>
                        </div>
                        {zoneStats.top3.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-primary/10">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                              Top {zoneStats.top3.length} cliente{zoneStats.top3.length > 1 ? 'i' : ''} per fatturato
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                              {zoneStats.top3.map((c, i) => (
                                <Link
                                  key={c.id}
                                  to={`/customers/${c.id}`}
                                  className="hover:underline"
                                >
                                  <span className="text-muted-foreground">{i + 1}.</span>{' '}
                                  <span className="font-medium">{c.name}</span>{' '}
                                  <span className="text-muted-foreground">— {euro(c.totalAmount)}</span>
                                </Link>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Priorità: canWrite > hasMapDrawing. cancelled → rosso "Riattiva". */}
              {!canWrite ? (
                <WriteBlockedBanner
                  feature="Drawing tools mappa + KPI di zona"
                  description="L'analisi geografica con zone disegnate resta accessibile solo con abbonamento attivo. Riattiva per ridisegnare le tue zone operative."
                />
              ) : !hasMapDrawing ? (
                <UpsellBanner
                  plan="studio"
                  feature="Drawing tools mappa + KPI di zona"
                  description="Disegna cerchi, rettangoli o poligoni sulla mappa per ottenere KPI di zona (clienti, fatturato vinto, conversion rate, top 3 per fatturato). Disponibile dal piano Studio."
                />
              ) : null}
              <Card className="overflow-hidden">
                <div style={{ height: '70vh', minHeight: '500px' }}>
                <MapContainer
                  center={ITALY_CENTER}
                  zoom={6}
                  scrollWheelZoom
                  style={{ width: '100%', height: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {hasMapDrawing && <GeomanControls initialZone={zone} onZoneChange={setZone} />}
                  <MarkerClusterGroup
                    key={Object.entries(statusFilter).map(([k, v]) => `${k}:${v ? 1 : 0}`).join('|')}
                    chunkedLoading
                    showCoverageOnHover={false}
                    maxClusterRadius={70}
                    spiderfyOnMaxZoom
                    iconCreateFunction={buildClusterIcon}
                  >
                    {visibleMarkers.map((c) => {
                      const total = visibleTotal(c, statusFilter);
                      const radius = radiusFor(total);
                      const slices = STATUS_ORDER.map(s => ({
                        status: s,
                        amount: statusFilter[s] ? c.byStatus[s].amount : 0,
                      }));
                      const statusAmounts: Record<StatusKey, number> = {
                        draft: statusFilter.draft ? c.byStatus.draft.amount : 0,
                        sent:  statusFilter.sent  ? c.byStatus.sent.amount  : 0,
                        won:   statusFilter.won   ? c.byStatus.won.amount   : 0,
                        lost:  statusFilter.lost  ? c.byStatus.lost.amount  : 0,
                      };
                      return (
                        <Marker
                          key={`${Object.values(statusFilter).join('')}-${c.id}`}
                          position={[c.latitude as number, c.longitude as number]}
                          icon={buildPieIcon({ radius, slices })}
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          {...({ statusAmounts } as any)}
                        >
                          <Popup>
                            <div className="space-y-1 min-w-[220px]">
                              <p className="font-semibold text-sm">{c.name}</p>
                              {c.city && (
                                <p className="text-xs text-muted-foreground">
                                  {[c.city, c.province].filter(Boolean).join(' (')}
                                  {c.province ? ')' : ''}
                                </p>
                              )}
                              <div className="text-xs space-y-0.5 pt-1 border-t mt-1">
                                <p>Preventivi: <strong>{c.estimatesCount}</strong></p>
                                {STATUS_ORDER.map(s => (
                                  c.byStatus[s].count > 0 && (
                                    <p key={s} className="flex items-center gap-1.5">
                                      <span
                                        className="inline-block w-2 h-2 rounded-full border border-gray-300"
                                        style={{ background: STATUS_COLOR[s] }}
                                      />
                                      <span>{STATUS_LABEL[s]}:</span>
                                      <strong>{euroFull(c.byStatus[s].amount)}</strong>
                                      <span className="text-muted-foreground">({c.byStatus[s].count})</span>
                                    </p>
                                  )
                                ))}
                                <p className="pt-1 border-t border-dashed">
                                  Totale: <strong>{euroFull(c.totalAmount)}</strong>
                                </p>
                              </div>
                              <Link
                                to={`/customers/${c.id}`}
                                className="text-xs text-primary underline pt-1 inline-block"
                              >
                                Apri scheda →
                              </Link>
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </MarkerClusterGroup>
                </MapContainer>
              </div>
              <CardContent className="py-3 text-xs text-muted-foreground flex items-center justify-between flex-wrap gap-2">
                <span>
                  <strong>{visibleMarkers.length}/{withCoords.length}</strong> clienti su mappa
                  {withoutCoords > 0 && ` (${withoutCoords} senza coordinate)`}
                </span>
                <span>Cluster automatici su zoom out · Disegna una zona dalla toolbar in alto a destra</span>
              </CardContent>
            </Card>
            </>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default CustomersMap;
